<template>
  <div class="file-uploader">
    <!-- Upload Area -->
    <div 
      class="border-2 border-dashed rounded-lg p-6 text-center transition-colors"
      :class="{
        'border-gray-300 hover:border-gray-400': !isDragOver && !hasFile,
        'border-blue-500 bg-blue-50': isDragOver,
        'border-green-500 bg-green-50': hasFile && !uploading,
        'border-yellow-500 bg-yellow-50': uploading
      }"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        @change="handleFileSelect"
        class="hidden"
      />

      <!-- Upload Icon and Text -->
      <div v-if="!hasFile && !uploading">
        <svg class="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
        </svg>
        <p class="text-lg font-medium text-gray-700 mb-2">
          {{ fileType === 'image' ? 'Upload Profile Photo' : 'Upload CV' }}
        </p>
        <p class="text-sm text-gray-500 mb-4">
          Drag and drop your file here, or click to browse
        </p>
        <button
          type="button"
          @click="openFileDialog"
          class="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
        >
          Choose File
        </button>
        <p class="text-xs text-gray-400 mt-2">
          {{ getFileTypeText() }}
        </p>
      </div>

      <!-- File Preview -->
      <div v-else-if="hasFile && !uploading" class="space-y-4">
        <div class="flex items-center justify-center">
          <!-- Image Preview -->
          <div v-if="fileType === 'image' && previewUrl" class="relative">
            <img :src="previewUrl" alt="Preview" class="w-28 h-36 object-contain object-center rounded-lg border-2 border-gray-200 bg-gray-100" @error="handleImageError">
            <div class="absolute -top-2 -right-2">
              <svg class="w-6 h-6 text-green-500 bg-white rounded-full" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
          
          <!-- PDF Icon -->
          <div v-else-if="fileType === 'pdf'" class="relative">
            <svg class="w-16 h-16 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
            </svg>
            <div class="absolute -top-2 -right-2">
              <svg class="w-6 h-6 text-green-500 bg-white rounded-full" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
        
        <div class="text-center">
          <p class="font-medium text-gray-700">{{ displayFileName }}</p>
          <p class="text-sm text-gray-500">{{ displayFileSize }}</p>
        </div>
        
        <div class="flex justify-center space-x-3">
          <button
            type="button"
            @click="openFileDialog"
            class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Replace
          </button>
          <button
            type="button"
            @click="removeFile"
            class="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
          >
            Remove
          </button>
        </div>
      </div>

      <!-- Upload Progress -->
      <div v-else-if="uploading" class="space-y-4">
        <svg class="w-12 h-12 mx-auto text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="font-medium text-gray-700">Uploading...</p>
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div 
            class="bg-blue-600 h-2 rounded-full transition-all duration-300"
            :style="{ width: uploadProgress + '%' }"
          ></div>
        </div>
        <p class="text-sm text-gray-500">{{ uploadProgress }}% complete</p>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-sm text-red-600">{{ errorMessage }}</p>
    </div>

    <!-- Success Message -->
    <div v-if="successMessage" class="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
      <p class="text-sm text-green-600">{{ successMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import axios from 'axios';
import type { SubmitFileValue, SubmitStoredFile } from '../types';

interface UploadedPreviewFile {
  id?: number;
  name: string;
  size: number;
  type: string;
  url?: string;
}

interface Props {
  modelValue?: SubmitFileValue;
  accept?: string;
  maxSize?: number; // in bytes
  fileType?: 'image' | 'pdf';
  required?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  accept: '*/*',
  maxSize: 10 * 1024 * 1024,
  fileType: 'pdf',
  required: false
});

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
  'upload': [file: SubmitStoredFile];
  'error': [error: string];
  'success': [message: string];
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const currentFile = ref<File | UploadedPreviewFile | null>(null);
const previewUrl = ref<string>('');
const uploading = ref(false);
const uploadProgress = ref(0);
const isDragOver = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const hasFile = computed(() => !!currentFile.value);
const displayFileName = computed(() => {
  if (!currentFile.value) {
    return '';
  }

  return currentFile.value instanceof File
    ? currentFile.value.name
    : currentFile.value.name;
});
const displayFileSize = computed(() => {
  if (!currentFile.value) {
    return '0 Bytes';
  }

  return formatFileSize(
    currentFile.value instanceof File
      ? currentFile.value.size
      : currentFile.value.size,
  );
});

const API = import.meta.env.VITE_API || '/api';

const isUploadedPreviewFile = (value: unknown): value is UploadedPreviewFile => {
  return !!value && typeof value === 'object' && !(value instanceof File) && 'name' in value;
};

const normalizeStoredFile = (value: SubmitStoredFile, fallbackUrl?: string): UploadedPreviewFile => ({
  id: value.id,
  name: value.filename || value.name || 'Unknown file',
  size: Number(value.size || 0),
  type: value.mimetype || value.type || (props.fileType === 'image' ? 'image/jpeg' : 'application/pdf'),
  url: value.url || value.path || fallbackUrl,
});

/**
 * Convert a possibly relative API URL (e.g., /api/files/123) to an absolute URL
 * using VITE_API for image previews.
 */
const makeAbsolute = (url: string): string => {
  if (!url) return '';
  if (/^https?:\/\//.test(url)) return url;
  if (url.startsWith('/api/')) {
    const base = API.replace(/\/api$/, '');
    return `${base}${url}`;
  }
  return url;
};

/**
 * Update the preview image URL. For server-provided URLs, ensure the
 * preview uses an absolute URL so it loads correctly during development.
 */
const updatePreview = () => {
  if (currentFile.value && props.fileType === 'image') {
    if (currentFile.value instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        previewUrl.value = e.target?.result as string;
      };
      reader.readAsDataURL(currentFile.value);
    } else if (isUploadedPreviewFile(currentFile.value) && currentFile.value.url) {
      previewUrl.value = makeAbsolute(currentFile.value.url);
    } else {
      previewUrl.value = '';
    }
  } else {
    previewUrl.value = '';
  }
};

/**
 * Fallback: if the direct URL fails to load in an <img>, fetch the
 * file as a Blob with credentials and display using an object URL.
 */
const handleImageError = async () => {
  try {
    const url = isUploadedPreviewFile(currentFile.value) ? currentFile.value.url : undefined;
    if (!url) return;
    const absolute = makeAbsolute(url);
    const response = await fetch(absolute, { credentials: 'include' });
    if (!response.ok) return;
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    previewUrl.value = objectUrl;
  } catch (e) {
    console.error('Preview fallback failed:', e);
  }
};

const openFileDialog = () => {
  fileInput.value?.click();
};

watch(() => props.modelValue, async (newFile) => {
  try {
    if (!newFile) {
      currentFile.value = null;
      previewUrl.value = '';
      return;
    }

    if (typeof newFile === 'string') {
      const trimmedFile = newFile.trim();
      if (trimmedFile === '') {
        currentFile.value = null;
        previewUrl.value = '';
        return;
      }

      try {
        const fileId = trimmedFile.split('/').pop();

        if (fileId && !Number.isNaN(Number.parseInt(fileId, 10))) {
          const response = await fetch(`${API}/files/info/${fileId}`, {
            credentials: 'include'
          });

          if (response.ok) {
            const fileInfo = await response.json() as SubmitStoredFile;
            currentFile.value = normalizeStoredFile(fileInfo, trimmedFile);
          } else {
            currentFile.value = normalizeStoredFile({ url: trimmedFile }, trimmedFile);
          }
        } else {
          currentFile.value = normalizeStoredFile({ url: trimmedFile }, trimmedFile);
        }
      } catch {
        currentFile.value = normalizeStoredFile({ url: trimmedFile }, trimmedFile);
      }
    } else {
      currentFile.value = normalizeStoredFile(newFile);
    }

    updatePreview();
  } catch {
    currentFile.value = null;
    previewUrl.value = '';
  }
}, { immediate: true });

const getFileTypeText = () => {
  const maxSizeMb = Math.round(props.maxSize / (1024 * 1024));

  if (props.fileType === 'image') {
    return `JPG, PNG formats (Max ${maxSizeMb} MB)`;
  } else if (props.fileType === 'pdf') {
    return `PDF format only (Max ${maxSizeMb} MB)`;
  }
  return `Max ${maxSizeMb} MB`;
};

const validateFile = (file: File): string | null => {
  if (file.size > props.maxSize) {
    return `File size must be less than ${Math.round(props.maxSize / (1024 * 1024))}MB`;
  }

  // Check file type
  if (props.fileType === 'image' && !file.type.startsWith('image/')) {
    return 'Please select an image file';
  }
  
  if (props.fileType === 'pdf' && !file.type.includes('pdf')) {
    return 'Please select a PDF file';
  }

  return null;
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    processFile(file);
  }
};

const handleDrop = (event: DragEvent) => {
  isDragOver.value = false;
  const file = event.dataTransfer?.files[0];
  if (file) {
    processFile(file);
  }
};

const processFile = async (file: File) => {
  clearMessages();
  
  const validationError = validateFile(file);
  if (validationError) {
    showError(validationError);
    return;
  }

  currentFile.value = file;
  updatePreview();

  await uploadFile(file);
};

/**
 * Upload the file to the server and emit the stored API URL string via v-model.
 */
const uploadFile = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('section_key', props.fileType === 'image' ? 'profile_image' : 'cv');
  formData.append('file_type', props.fileType === 'image' ? 'profile_image' : 'cv');

  uploading.value = true;
  uploadProgress.value = 0;

  try {
    const { data } = await axios.post(`${API}/files/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      }
    });
    
    showSuccess(`${props.fileType === 'image' ? 'Photo' : 'CV'} uploaded successfully!`);
    
    const urlString = (data?.url && typeof data.url === 'string')
      ? data.url
      : (data?.id ? `/api/files/${data.id}` : '');
    if (urlString) {
      emit('update:modelValue', urlString);
    }

    emit('upload', data);
  } catch (error: any) {
    console.error('Upload failed:', error);
    const message = error.response?.data?.error || 'Upload failed. Please try again.';
    showError(message);
    currentFile.value = null;
    previewUrl.value = '';
    emit('update:modelValue', null);
  } finally {
    uploading.value = false;
    uploadProgress.value = 0;
  }
};

const removeFile = () => {
  currentFile.value = null;
  previewUrl.value = '';
  emit('update:modelValue', null);
  clearMessages();
  
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const showError = (message: string) => {
  errorMessage.value = message;
  successMessage.value = '';
  emit('error', message);
  setTimeout(clearMessages, 5000);
};

const showSuccess = (message: string) => {
  try {
    successMessage.value = message;
    errorMessage.value = '';
    emit('success', message);
    setTimeout(clearMessages, 3000);
  } catch (error) {
    console.error('Error in showSuccess:', error);
  }
};

const clearMessages = () => {
  errorMessage.value = '';
  successMessage.value = '';
};
</script>

<style scoped>
@reference "../styles.css";

.file-uploader {
  @apply w-full;
}
</style>
